import type { Dispatch, SetStateAction } from 'react';
import { Fragment, useEffect, useState } from 'react';
// @ts-ignore - no type declarations for 'react-html-parser'
import ReactHtmlParser from 'react-html-parser';
import { useTranslation } from 'react-i18next';

import { Typography } from '@mui/material';
import { getIn } from 'formik';

import { Control, FormCard } from '../../../../components';
import { CARD_CATEGORIES } from '../../../../shared/cardConfig/constants';
import {
  getAddressConfigByCategory as getAddressConfigByCategoryRaw,
  getAddressFieldConfig as getAddressFieldConfigRaw
} from '../../../../shared/cardConfig/helpers';
import { taxExemptUploadDialog } from '../../../../shared/dialogConfigs';
import { SHIP_TO_ADDRESS_FIELDS } from '../../../../shared/fieldConfig/constants';
import { clearFieldError } from '../../../../store/fieldError';
import { CONTROL_TYPE } from '../../../../utils/constants';
import { handleRemoveFile, openUploadDialog } from '../../../../utils/fileUploadHelpers';
import { VALIDATION } from '../../../../utils/validation';
import AddTaxNumberButton from '../../../AddTaxNumberButton';
import AttachmentUpload from '../../../AttachmentUpload';
import type {
  AddressFieldConfig,
  CardDispatch,
  CardErrorMustContain,
  CardField,
  CardFieldComponent,
  CardFieldProps,
  CardStore,
  TFunc
} from '../../types';
import DeleteShipTo from '../DeleteShipTo';

const category = CARD_CATEGORIES.shipTo;

// These helpers are authored in JS with JSDoc types that are stricter than
// these call sites' runtime usage; keep the original behaviour with loose
// signatures.
const getAddressFieldConfig = getAddressFieldConfigRaw as unknown as (
  field: string,
  t: TFunc,
  disabled?: boolean,
  maxCharacters?: number | null,
  category?: string,
  isOptional?: boolean,
  controlType?: string
) => AddressFieldConfig;
const getAddressConfigByCategory = getAddressConfigByCategoryRaw as (
  category: string,
  t: TFunc,
  store?: CardStore,
  isCardOptional?: boolean,
  multiShipToIndex?: number,
  disabled?: boolean
) => AddressFieldConfig[];

function DnbNumber({ isReview, t, index }: CardFieldProps) {
  const field = 'dnbNumber';
  const name = `${category}[${index}].address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.dnbNumber.label'),
    validations: [{ type: VALIDATION.numberOfDigits, param: 9 }],
    disabled: isReview,
    placeholder: t('address.dnbNumber.placeholder'),
    footer: (
      <Typography
        variant="body2"
        sx={{
          mt: '4px',
          '& a': {
            fontWeight: '400 !important',
            fontSize: '12px !important',
            lineHeight: '16px !important'
          }
        }}
      >
        {t('address.dnbNumber.footer')}
        <a className="sprout-link base" href="https://www.dnb.com/duns-number/lookup.html">
          {' '}
          https://www.dnb.com/duns-number/lookup.html
        </a>
      </Typography>
    )
  };

  return <Control {...config} />;
}

function TaxNumber1({ t, disabled, index = 0, store }: CardFieldProps) {
  const { customer } = store;
  const { [category]: multiShipToData = [] } = customer;
  const { address } = multiShipToData[index];
  const { naturalPerson } = address;
  const { region = '', countryCode = '' } = customer;
  const isTaxNumber1Required = region.toLowerCase() === 'latam' && countryCode === 'MX' ? !naturalPerson : false;

  const field = 'taxNumber1';
  const name = `${category}[${index}].address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber1.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    required: isTaxNumber1Required,
    disabled,
    placeholder: t('address.taxNumber1.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber2({ t, disabled, index }: CardFieldProps) {
  const field = 'taxNumber2';
  const name = `${category}[${index}].address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber2.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber2.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber3({ t, disabled, index }: CardFieldProps) {
  const field = 'taxNumber3';
  const name = `${category}[${index}].address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber3.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber3.placeholder')
  };

  return <Control {...config} />;
}

function TaxNumber4({ t, disabled, index }: CardFieldProps) {
  const field = 'taxNumber4';
  const name = `${category}[${index}].address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t('address.taxNumber4.label'),
    validations: [{ type: VALIDATION.numberOfAlphaNumeric }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled,
    placeholder: t('address.taxNumber4.placeholder')
  };

  return <Control {...config} />;
}

function Address({
  isReview,
  store: { editMode: { shipTo } = {} } = {},
  t,
  index,
  hideSameAs,
  store,
  hideSameAsBillTo
}: CardFieldProps) {
  const field = `address`;
  const name = `${category}[${index}].${field}`;
  const {
    customer: { region = '', countryCode = '' }
  } = store;
  let cardSpecificFields;
  switch (region.toLowerCase()) {
    case 'latam':
      if (countryCode.toLowerCase() === 'mx') {
        cardSpecificFields = [
          getAddressFieldConfig('naturalPerson', t, isReview && !shipTo, null, category, false, CONTROL_TYPE.checkbox),
          getAddressFieldConfig('legalName', t, isReview && !shipTo, 105, category, true),
          getAddressFieldConfig('tradeDbaName', t, isReview && !shipTo, 35, category)
        ];
      } else {
        cardSpecificFields = [
          getAddressFieldConfig('legalName', t, isReview && !shipTo, 105, category, true),
          getAddressFieldConfig('tradeDbaName', t, isReview && !shipTo, 35, category)
        ];
      }
      break;
    case 'emea':
      cardSpecificFields = [
        getAddressFieldConfig('tradeDbaName', t, isReview && !shipTo, 35, category, true),
        getAddressFieldConfig('legalName', t, isReview && !shipTo, 105, category, true)
      ];
      break;
    default:
      cardSpecificFields = [
        getAddressFieldConfig('tradeDbaName', t, isReview && !shipTo, 35, category),
        getAddressFieldConfig('legalName', t, isReview && !shipTo, 105, category, true)
      ];
      break;
  }
  const config = {
    disabled: isReview,
    category,
    name,
    showSoldTo: true,
    showBillTo: hideSameAsBillTo ? false : true,
    showNameFields: true,
    nameFieldPrefix: 'Ship To',
    showCareOfNameField: false,
    type: CONTROL_TYPE.address,
    hideSameAs,
    label: t(`${category}.${category}.label`),
    addressFields: [...cardSpecificFields, ...getAddressConfigByCategory(`shipTo`, t, store, false, index)]
  };

  return <Control {...config} />;
}

function TaxExempt({ isReview, t, index = 0, dispatch, apiService, store }: CardFieldProps) {
  const field = `taxExempt`;
  const name = `${category}[${index}].address.${field}`;

  const config = {
    disabled: isReview,
    category,
    name,
    field,
    type: CONTROL_TYPE.checkbox,
    label: t('address.taxExempt.label')
  };
  const { customer } = store;
  const { [category]: multiShipToData = [] } = customer;
  const {
    address: { taxExempt }
  } = multiShipToData[index];
  const attachments = store.attachments.uploads.taxExemptAttachment.files;
  const attachmentsConfig = {
    store,
    category,
    field: 'taxExemptAttachment',
    label: t('address.taxExempt.attachments.label'),
    type: CONTROL_TYPE.attachmentList,
    attachments,
    handleRemove: handleRemoveFile(dispatch as CardDispatch, apiService),
    handleUpload: openUploadDialog({
      dispatch: dispatch as CardDispatch,
      apiService,
      // Dialog config's published DialogEvent types don't line up with the
      // upload/cancel callbacks; runtime is correct.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- dialog config uses DialogEvent handlers that don't match the helper's UploadDialog type; runtime is correct
      uploadDialog: taxExemptUploadDialog as any,
      field: 'taxExemptAttachment'
    }),
    handleDownload: (hcId: string | number | undefined, fileName: string) =>
      apiService.downloadFile(hcId, fileName, dispatch),
    shouldShow: getIn(store.customer, name) && !isReview,
    dispatch: dispatch as CardDispatch
  };

  return (
    <Fragment>
      <Control {...config} />
      {taxExempt && (
        <Typography
          variant="body2"
          sx={{
            mt: '4px',
            '& a': {
              color: '#00843d !important',
              textDecoration: 'underline !important',
              fontWeight: '500 !important',
              fontSize: '12px !important',
              lineHeight: '16px !important',
              cursor: 'pointer',
              transition: 'color 0.2s',
              outline: 'none',
              '&:hover, &:focus': {
                color: 'var(--sprout-palette-green-900) !important',
                outline: 'none'
              }
            }
          }}
        >
          {ReactHtmlParser(t('address.taxExempt.TaxExemptCheckbox'))}
        </Typography>
      )}
      <AttachmentUpload {...attachmentsConfig} />
    </Fragment>
  );
}

function PhoneNumber1({ disabled, t, index, required }: CardFieldProps) {
  const field = 'telephone1';
  const name = `${category}[${index}].address.${field}`;
  const config = {
    name,
    category,
    field,
    required: required,
    label: t(`address.telephone1.label`),
    placeholder: t(`address.telephone1.placeholder`),
    validations: [{ type: VALIDATION.number }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled
  };

  return <Control {...config} />;
}

function PhoneNumber2({ disabled, t, index }: CardFieldProps) {
  const field = 'telephone2';
  const name = `${category}[${index}].address.${field}`;
  const config = {
    name,
    category,
    field,
    label: t(`address.telephone2.label`),
    placeholder: t(`address.telephone2.placeholder`),
    validations: [{ type: VALIDATION.number }, { type: VALIDATION.maxCharacters, param: 16 }],
    disabled
  };

  return <Control {...config} />;
}

function Emails({ isReview, index, t }: CardFieldProps) {
  const field = 'email';
  const name = `${category}[${index}].address.${field}`;
  const config = {
    name,
    field: 'email',
    category,
    label: t('address.email.label'),
    type: CONTROL_TYPE.inputWithTags,
    validations: [
      { type: VALIDATION.array, param: [{ type: VALIDATION.email }] },
      { type: VALIDATION.maxCharacters, param: 255 }
    ],
    onAddTag: ({ store, dispatch }: { store: CardStore; dispatch: CardDispatch }) => {
      // If there is an error on the parent ('invoice delivery') but an email has been entered, clear the error on the parent
      if (typeof store.fieldError[name] === 'string') {
        dispatch(clearFieldError(name));
      }
    },
    placeholder: t('address.email.placeholder'),
    disabled: isReview
  };
  return <Control {...config} />;
}

interface RenderTaxNumbersArgs {
  currentTaxNumber: number;
  TotalTaxNumberCount?: number;
  disableTaxButton: number;
  fields?: CardField[];
  controlMap: Record<string, CardFieldComponent>;
  t: TFunc;
  isReview?: boolean;
  props: ShipToAddressCardProps;
  handleAddTaxNumber: (setCurrentTaxNumber?: Dispatch<SetStateAction<number>>) => void;
  setCurrentTaxNumber: Dispatch<SetStateAction<number>>;
}

function renderTaxNumbers({
  currentTaxNumber,
  TotalTaxNumberCount,
  disableTaxButton,
  fields,
  controlMap,
  t,
  isReview,
  props,
  handleAddTaxNumber,
  setCurrentTaxNumber // Receive setCurrentTaxNumber as a prop
}: RenderTaxNumbersArgs) {
  return fields?.map(({ key, ...fieldProps }: CardField) => {
    const Component = controlMap[key];
    if (key.startsWith('taxNumber')) {
      const index = parseInt(key.slice(-1)) - 1;
      if (index < currentTaxNumber) {
        return (
          <>
            <Component t={t} disabled={isReview} {...fieldProps} {...props} key={key} />
            {!window.location.href.includes('review') &&
              currentTaxNumber === index + 1 &&
              currentTaxNumber !== TotalTaxNumberCount && (
                <AddTaxNumberButton
                  disabled={!(disableTaxButton + 1 > currentTaxNumber)}
                  onClick={() => handleAddTaxNumber(setCurrentTaxNumber)}
                  t={t}
                />
              )}
          </>
        );
      }
    } else {
      return <Component t={t} disabled={isReview} {...fieldProps} {...props} key={key} />;
    }
    return <></>;
  });
}

interface ShipToAddressCardProps {
  isReview?: boolean;
  index?: number;
  store?: CardStore;
  fields?: CardField[];
  errorMustContain?: CardErrorMustContain;
  [key: string]: unknown;
}

function ShipToAddressCard(props: ShipToAddressCardProps) {
  const { isReview, index = 0, store, fields, errorMustContain } = props;
  const { t } = useTranslation();
  const address =
    store && store.customer.shipTo && store.customer.shipTo[index] && store.customer.shipTo[index].address;

  const TotalTaxNumberCount = fields && fields.filter((obj: CardField) => obj.key.startsWith('taxNumber')).length;
  const backendTaxNumberCount =
    address && Object.keys(address).filter((key: string) => key.startsWith('taxNumber')).length;
  const [currentTaxNumber, setCurrentTaxNumber] = useState(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 1);
  const [disableTaxButton, setDisableTaxButton] = useState(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 0);
  useEffect(() => {
    const backendTaxNumberCount = Object.keys(address).filter((key: string) => key.startsWith('taxNumber')).length;
    setDisableTaxButton(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 0);
    setCurrentTaxNumber(backendTaxNumberCount !== 0 ? backendTaxNumberCount : 1);
  }, [address, setCurrentTaxNumber]);

  const cardProps = {
    cardId: category,
    title: t('shipTo.shipTo.title'),
    subtitle: t('shipTo.shipTo.subtitle'),
    image: '/food/onboarding/assets/Ship_To_Address.svg',
    isReview,
    errorMustContain,
    ...props
  };

  const handleAddTaxNumber = () => {
    if (disableTaxButton + 1 > currentTaxNumber) {
      setCurrentTaxNumber((prevNum: number) => prevNum + 1);
    }
  };

  const controlMap: Record<string, CardFieldComponent> = {
    [SHIP_TO_ADDRESS_FIELDS.address]: Address,
    [SHIP_TO_ADDRESS_FIELDS.taxExempt]: TaxExempt,
    [SHIP_TO_ADDRESS_FIELDS.dnbNumber]: DnbNumber,
    [SHIP_TO_ADDRESS_FIELDS.email]: Emails,
    [SHIP_TO_ADDRESS_FIELDS.taxNumber1]: TaxNumber1,
    [SHIP_TO_ADDRESS_FIELDS.taxNumber2]: TaxNumber2,
    [SHIP_TO_ADDRESS_FIELDS.taxNumber3]: TaxNumber3,
    [SHIP_TO_ADDRESS_FIELDS.taxNumber4]: TaxNumber4,
    [SHIP_TO_ADDRESS_FIELDS.telephone1]: PhoneNumber1,
    [SHIP_TO_ADDRESS_FIELDS.telephone2]: PhoneNumber2
  };

  return (
    <FormCard {...cardProps}>
      {!isReview && index > -1 && <DeleteShipTo {...cardProps} index={index} />}
      {renderTaxNumbers({
        currentTaxNumber,
        TotalTaxNumberCount,
        disableTaxButton,
        fields,
        controlMap,
        t,
        isReview,
        props,
        handleAddTaxNumber, // Pass handleAddTaxNumber as a prop
        setCurrentTaxNumber // Pass setCurrentTaxNumber as a prop
      })}
    </FormCard>
  );
}

export default ShipToAddressCard;
